import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Label, Input, CardBody, Button, Modal, ModalBody, ModalFooter, ModalHeader, Form} from "reactstrap";
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import {addUser} from "../../slices/user/add/thunk";
import * as Yup from "yup";
import { useFormik } from "formik";
import {appName} from "../../Components/constants/default";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {getUserList} from "../../slices/user/list/thunk";
import {deleteUser} from "../../slices/user/delete/thunk";
import {userDeleteReset} from "../../slices/user/delete/reducer";
import {userAddReset} from "../../slices/user/add/reducer";
import {validators} from "../../helpers/ValidatorHelper";
import {authUserManager} from "../../helpers/api_helper";


const UserList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAdmin = authUserManager.isAdmin();

  document.title = t('Users') + ' | ' + appName
  const handleUserClick = (id) => {
    navigate(`/admin/user/${id}`);
  };
  const dispatch = useDispatch();

  const handleGetLink = (id) => {
    navigator.clipboard.writeText(window.location.origin + `/event/${id}`)
  };

  const formatAmount = (amount) => `${amount ?? 0} PLN`;

  //User List
  const userList = useSelector((s) => s.UserList.list);

  useEffect(() => {
    if (isAdmin) {
      dispatch(getUserList());
    }
  }, [dispatch, isAdmin]);

  //ROLE FILTER
  const [roleFilter, setRoleFilter] = useState('');

  const roleOptions = useMemo(() => {
    const roles = new Set();
    userList.forEach((user) => (user.roles || []).forEach((role) => roles.add(role)));
    return Array.from(roles).sort();
  }, [userList]);

  const filteredUserList = useMemo(() => {
    if (!roleFilter) return userList;
    return userList.filter((user) => (user.roles || []).includes(roleFilter));
  }, [userList, roleFilter]);

  //MODAL ADD
  const [modal_list, setmodal_list] = useState(false);
  const tog_list = () => {
    setmodal_list(!modal_list);
  };

  //MODAL DELETE
  const [modal_delete, setmodal_delete] = useState(false);
  const tog_delete = () => {
    setmodal_delete(!modal_delete);
  };
  const [deleteId, setDeleteId] = useState(null);

  const handleDeleteModal = (id) => {
    setmodal_delete(true);
    setDeleteId(id);
  };

  const handleDeleteItem = () => {
    dispatch(deleteUser(deleteId))
  };

  //EVENT DELETE
  const selectDeleteEvent = createSelector(
    (state) => state.UserDelete,
    (slice) => ({
      deleteUserSuccess: slice.success,
      deleteUserError: slice.error,
      deleteUserErrorMessage: slice.errorMessage,
    })
  );
  const {
    deleteUserSuccess,
    deleteUserError,
    deleteUserErrorMessage
  } = useSelector(selectDeleteEvent);

  useEffect(() => {
    if (deleteUserSuccess) {
      setmodal_delete(false);
      dispatch(getUserList());
      dispatch(userDeleteReset())
    }
  }, [deleteUserSuccess, deleteUserError]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
      username: '',
      plainPassword: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().required(t("Please Enter Email"))
        .min(7, t('At least 7 chars'))
        .max(40, t('Up to 30 chars')),
      username: validators.username(t),
      plainPassword: validators.password(t)
    }),
    onSubmit: async (values) => {
        dispatch(addUser(values));
    }
  });

  //User Add
  const selectAddUser = createSelector(
    (state) => state.UserAdd,
    (slice) => ({
      userAddSuccess: slice.success,
      userAddError: slice.violations,
    })
  );
  const {
    userAddError,
    userAddSuccess,
  } = useSelector(selectAddUser);

  useEffect(() => {
    if (userAddError?.length > 0) {
      userAddError.forEach((violation) => {
        validation.setFieldError(violation.propertyPath, t(violation.message));
      });
    }
  }, [userAddError]);


  useEffect(() => {
    if (userAddSuccess) {
      validation.resetForm();
      setmodal_list(false);
      dispatch(getUserList());
      dispatch(userAddReset());
    }
  }, [userAddSuccess]);


  if (!isAdmin) {
    const eventId = authUserManager.getAdminEventId();
    return <Navigate to={eventId ? `/admin/event/${eventId}` : '/admin/login'} replace />;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t('Users list')} pageTitle={t('Users')} />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="listjs-table" id="customerList">
                    <Row className="g-4 mb-3 align-items-end">
                      <Col sm="auto">
                        <Button color="success" className="btn btn-label me-1 btn-md w-sm btn-border" onClick={() => tog_list()} id="create-btn">
                          <i className="ri-add-circle-line label-icon align-middle fs-16 me-2"></i>
                          {t('Add')}
                        </Button>
                      </Col>
                      <Col sm={4} md={3}>
                        <Label className="form-label" htmlFor="roleFilter">{t('Roles')}</Label>
                        <Input
                          type="select"
                          id="roleFilter"
                          name="roleFilter"
                          value={roleFilter}
                          onChange={(e) => setRoleFilter(e.target.value)}
                        >
                          <option value="">{t('All roles')}</option>
                          {roleOptions.map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </Input>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table className="table align-middle table-nowrap table-sm" id="userTable">
                        <thead className="table-light">
                        <tr>
                          <th>{t('Email')}</th>
                          <th >{t('Username')}</th>
                          <th >{t('Roles')}</th>
                          <th className="text-end">{t('Paid tips')}</th>
                          <th className="text-end">{t('Unpaid tips')}</th>
                          <th>{t('Bank account')}</th>
                          <th>{t('Phone number')}</th>
                          <th>{t('Last Login')}</th>
                          <th></th>
                        </tr>
                        </thead>
                        <tbody className="list form-check-all">
                        {filteredUserList.map((user) => (
                          <tr key={user.id}>
                            <td className="d-md-table-cell">
                              <button
                                type="button"
                                className="btn btn-link p-0 align-baseline text-start fw-medium"
                                onClick={() => handleUserClick(user.id)}
                              >
                                {user.email}
                              </button>
                            </td>
                            <td
                              className="d-md-table-cell">
                              {user.username}
                            </td>
                            <td className="d-md-table-cell">
                              {user.roles.join(', ')}
                            </td>
                            <td className="d-md-table-cell text-end text-success">
                              {formatAmount(user.paidTipsAmount)}
                            </td>
                            <td className="d-md-table-cell text-end text-warning">
                              {formatAmount(user.unpaidTipsAmount)}
                            </td>
                            <td className="d-md-table-cell">
                              {user.bankAccount || '-'}
                            </td>
                            <td className="d-md-table-cell">
                              {user.phoneNumber || '-'}
                            </td>
                            <td className="d-md-table-cell">
                              {user.lastLogin}
                            </td>

                            <td>
                              <div className="d-flex gap-2">
                                <button type="button" className="btn btn-sm btn-warning btn-icon waves-effect waves-light" onClick={() => handleUserClick(user.id)}><i
                                  className="ri-edit-fill"></i></button>
                                <button onClick={() => handleGetLink(user.event.id)} type="button" className="btn btn-sm btn-primary btn-icon waves-effect waves-light"><i
                                  className="ri-link-m"></i></button>
                                {/*<button type="button" className="btn btn-sm btn-secondary btn-icon waves-effect waves-light"><i*/}
                                {/*  className="ri-qr-code-fill"></i></button>*/}
                                <button className="btn btn-sm btn-danger remove-item-btn"
                                        onClick={() => handleDeleteModal(user.id)} data-bs-toggle="modal"
                                        data-bs-target="#deleteRecordModal"><i className="ri-delete-bin-5-line"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        </tbody>
                      </table>
                      <div className="noresult" style={{ display: "none" }}>
                        <div className="text-center">
                          <lord-icon src="https://cdn.lordicon.com/msoeawqm.json" trigger="loop"
                                     colors="primary:#121331,secondary:#08a88a" style={{ width: "75px", height: "75px" }}>
                          </lord-icon>
                          <h5 className="mt-2">{t('Sorry! No Result Found')}</h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add Modal */}
      <Modal isOpen={modal_list} toggle={() => { tog_list(); }} centered >
        <ModalHeader className="bg-light p-3" toggle={() => { tog_list(); }}>{t('Add User')}</ModalHeader>
        <Form className="tablelist-form" onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
        }}>
          <ModalBody>
            <div className="mb-3">
              <Label className="form-label">{t("Username")}</Label>
              <Input
                type="text" id="username" name="username"
                className={`form-control ${validation.touched.username && validation.errors.username ? 'is-invalid' : ''}`}
                placeholder={t("Enter Username")}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.username || ""}/>
              {validation.touched.username && validation.errors.username && (
                <div className="invalid-feedback">{validation.errors.username}</div>
              )}
            </div>

            <div className="mb-3">
              <Label className="form-label">{t("Email")}</Label>
              <Input
                type="text" id="email" name="email"
                className={`form-control ${validation.touched.email && validation.errors.email ? 'is-invalid' : ''}`}
                placeholder={t("Enter Email")}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.email || ""}/>
              {validation.touched.email && validation.errors.email && (
                <div className="invalid-feedback">{validation.errors.email}</div>
              )}
            </div>

            <div className="mb-3">
              <Label className="form-label">{t("Password")}</Label>
              <Input
                type="text" id="plainPassword" name="plainPassword"
                className={`form-control ${validation.touched.plainPassword && validation.errors.plainPassword ? 'is-invalid' : ''}`}
                placeholder={t("Enter Password")}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.plainPassword || ""}/>
              {validation.touched.plainPassword && validation.errors.plainPassword && (
                <div className="invalid-feedback">{validation.errors.plainPassword}</div>
              )}
            </div>

          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button type="button" className="btn btn-label btn-light"  onClick={() => setmodal_list(false)}>
                <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>
                {t('Close')}
              </button>
              <button type="submit" className="btn btn-label btn-success" id="add-btn">
                <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>
                {t('Save')}
              </button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Remove Modal */}
      <Modal isOpen={modal_delete} toggle={() => { tog_delete(); }} className="modal zoomIn" id="deleteRecordModal" centered >
        <ModalBody>
          <div className=" text-center pt-2 fs-15 mx-4 mx-sm-5">
            <p className="text-muted mx-4 mb-1">{t('Are you sure You want to remove this item?')}</p>
            {deleteUserErrorMessage && <div className="alert alert-danger mb-xl-0 p-1" role="alert">{deleteUserErrorMessage}</div>}
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button type="button" className="btn w-sm btn-danger" onClick={() => handleDeleteItem()} id="delete-record">{t('Remove')}</button>
            <button type="button" className="btn w-sm btn-light"
                    onClick={() => {
                      setmodal_delete(false);
                      dispatch(userDeleteReset())
                    }}
            >{t('Close')}
            </button>
          </div>
        </ModalBody>
      </Modal>

    </React.Fragment>
  );
};

export default UserList;
